import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Typography,
  Button,
  TextField,
  useMediaQuery,
  Grid,
  CircularProgress,
} from "@mui/material";

import MicIcon from "@mui/icons-material/Mic";
import { BackButton, beautifyGradeText, checkLessonLimit } from "./functions";
import { AIModelSelect } from "./TestComponents2";
import AssessmentStyleSelector from "./AssessmentStyleSelector";

import { AIModelInfoTooltip } from "./AIModelInfoDialog";
import QuestionMarkIcon from "@mui/icons-material/Help";

import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  or,
  and,
  doc,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import StarIcon from "@mui/icons-material/Star";
import GavelIcon from "@mui/icons-material/Gavel";
import { VideoPlayer } from "./HelpDialogItems";

import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import BalanceIcon from "@mui/icons-material/Balance";
import SearchIcon from "@mui/icons-material/Search";

import LimitDialog from "./LimitDialog";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import CloseIcon from "@mui/icons-material/Close";
import { db } from "./firebase";

import CreateAssessmentStyleDialog from "./CreateAssessmentStyleDialog";
import DeleteIcon from "@mui/icons-material/DeleteOutline";

import { useSnackbar } from "./SnackbarHandler";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GradeSelector, {
  assessmentInstructions,
  gradeInstructions,
} from "./GradeSelector";

import { CheckCircleOutline } from "@mui/icons-material";
import InfoBox from "./InfoBox";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WordIcon from "./WordIcon2019";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AnimatedStep } from "./Animations";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import NotesIcon from "@mui/icons-material/Notes";
import {
  FormControl,
  MenuItem,
  Select as MUISelect,
  InputLabel,
} from "@mui/material";
import { getStyles } from "../index";
import TableChartIcon from "@mui/icons-material/TableChart";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useTheme } from "@mui/material/styles";
import CreateClassDialog from "./CreateClassDialog";

import mammoth from "mammoth";
import { FileUpload } from "@mui/icons-material";

import GenerateIcon from "@mui/icons-material/AutoAwesome";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import LanguageSelector from "./LanguageSelector";
import CustomInputSwitch from "./CustomInputSwitch";
import { callApi } from "./api";

import { trackLesson } from "./functions";
import SegmentIcon from "@mui/icons-material/Segment";
import Logger from "./Logger";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RubricsDrawer from "./RubricsDrawer";

import ProgressBar from "./ProgressBar"; // Import the ProgressBar component

const EssayUpload = ({ user }) => {
  const [educationLevel, setEducationLevel] = useState("Gymnasium");
  const [selectedRubric, setSelectedRubric] = useState("");
  const [language, setLanguage] = useState("svenska");
  const [essayText, setEssayText] = useState("");
  const [file, setFile] = useState(null);
  const [rubrics, setRubrics] = useState([]);
  const [isManualEntry, setIsManualEntry] = useState(false);

  const [customAssessmentStyles, setCustomAssessmentStyles] = useState([]);
  const [isCreateStyleDialogOpen, setIsCreateStyleDialogOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [gradingResults, setGradingResults] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const { showSnackbar } = useSnackbar();
  const [essayName, setEssayName] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const styles = getStyles(theme);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [isRubricsDialogOpen, setIsRubricsDialogOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [grade, setGrade] = useState("åk9");
  const [viewingRubric, setViewingRubric] = useState(null);
  const [userRubrics, setUserRubrics] = useState([]);
  const [standardRubrics, setStandardRubrics] = useState([]);
  const [isLoadingRubrics, setIsLoadingRubrics] = useState(true);
  const [progressLabel, setProgressLabel] = useState("Förbereder...");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isRubricsDrawerOpen, setIsRubricsDrawerOpen] = useState(false);

  const [selectedClass, setSelectedClass] = useState("");
  const [classes, setClasses] = useState([]);
  const [isCreateClassDialogOpen, setIsCreateClassDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState("Studera.AI");

  const [files, setFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({}); // Track progress for each file
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [lastProcessedDocRef, setLastProcessedDocRef] = useState(null);
  const [navigationDialogOpen, setNavigationDialogOpen] = useState(false);
  const [assessmentStyle, setAssessmentStyle] = useState("balanced");

  const [overallProgress, setOverallProgress] = useState(0);

  const [assessmentText, setAssessmentText] = useState(
    assessmentInstructions[grade],
  );

  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  // ADD THESE NEW STATES
  const [manualTexts, setManualTexts] = useState([
    { id: Date.now(), name: "", content: "" },
  ]);

  // ADD THESE NEW FUNCTIONS
  const addManualText = () => {
    setManualTexts((prev) => [
      ...prev,
      { id: Date.now(), name: "", content: "" },
    ]);
  };

  const removeManualText = (id) => {
    if (manualTexts.length > 1) {
      setManualTexts((prev) => prev.filter((text) => text.id !== id));
    }
  };

  const updateManualText = (id, field, value) => {
    setManualTexts((prev) =>
      prev.map((text) => (text.id === id ? { ...text, [field]: value } : text)),
    );
  };

  const renderManualTextSection = () => (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {manualTexts.map((text, index) => (
          <Box
            key={text.id}
            sx={{
              border: "2px dashed",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)",
              borderRadius: 2,
              p: 3,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Text {index + 1}
              </Typography>
              {manualTexts.length > 1 && (
                <IconButton
                  onClick={() => removeManualText(text.id)}
                  size="medium"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.5)"
                        : "rgba(0,0,0,0.5)",
                    "&:hover": {
                      color: "#EF4444",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(239, 68, 68, 0.1)"
                          : "rgba(239, 68, 68, 0.05)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 24 }} />
                </IconButton>
              )}
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
              Namn:
            </Typography>
            <TextField
              fullWidth
              value={text.name}
              onChange={(e) =>
                updateManualText(text.id, "name", e.target.value)
              }
              placeholder="Namn på texten"
              required
              sx={{ mb: 2, ...styles.whiteBg }}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
              Text:
            </Typography>
            <TextField
              multiline
              rows={8}
              fullWidth
              value={text.content}
              onChange={(e) =>
                updateManualText(text.id, "content", e.target.value)
              }
              placeholder="Skriv eller klistra in texten här"
              required
              sx={{ ...styles.whiteBg }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Button
          variant="print"
          onClick={addManualText}
          startIcon={<AddIcon />}
          sx={{ width: "250px" }}
        >
          Lägg till en text till
        </Button>
      </Box>
    </>
  );

  const handleBackdropClick = (event) => {
    event.stopPropagation();

    showSnackbar("Genereringen är snart klar. Vänligen vänta.", "info");
  };
  // Video details
  const videoUrl =
    "https://sportkuriren.se/wp-content/uploads/bedomning-resultattavla-snabb-2.mp4";
  const posterUrl =
    "https://sportkuriren.se/wp-content/uploads/bedomning-resultattavla-snabb.png";

  useEffect(() => {
    Logger.log("selectedRubric: ", selectedRubric);

    if (currentStep === 2 && selectedRubric) {
      setCurrentStep(3);
    }
  }, [currentStep, selectedRubric]);

  useEffect(() => {
    const fetchCustomAssessmentStyles = async () => {
      try {
        const stylesCollection = collection(db, "assessmentStyles");
        const q = query(stylesCollection, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const stylesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCustomAssessmentStyles(stylesData);
      } catch (error) {
        console.error("Error fetching custom assessment styles:", error);
        showSnackbar(
          "Ett fel uppstod vid hämtning av bedömningsstilar",
          "error",
        );
      }
    };

    fetchCustomAssessmentStyles();
  }, [user.uid]);

  useEffect(() => {
    setAssessmentText(assessmentInstructions[grade]);

    Logger.log("GRAAAAAAAADE: ", grade);
    Logger.log("assessmentInstructions: ", assessmentInstructions[grade]);
  }, [grade]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classesCollection = collection(db, "classes");
        const q = query(classesCollection, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const classesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClasses(classesData);
      } catch (error) {
        console.error("Error fetching classes:", error);
        showSnackbar("Ett fel uppstod vid hämtning av klasser", "error");
      }
    };

    fetchClasses();
  }, [user.uid]);

  useEffect(() => {
    Logger.log("selectedRubric: ", selectedRubric);
  }, [selectedRubric]);

  const handleDeleteClass = async (classId, event) => {
    event.stopPropagation();

    try {
      await deleteDoc(doc(db, "classes", classId));
      setClasses((prev) => prev.filter((cls) => cls.id !== classId));

      // Reset selected class if the deleted one was selected
      if (selectedClass === classId) {
        setSelectedClass("");
      }

      showSnackbar("Klass raderad", "success");
    } catch (error) {
      console.error("Error deleting class:", error);
      showSnackbar("Ett fel uppstod vid radering av klass", "error");
    }
  };

  const handleClassCreated = (newClass) => {
    setClasses((prev) => [...prev, newClass]);

    setSelectedClass(newClass.id);
    setIsCreateClassDialogOpen(false);

    showSnackbar("Klassen är skapad och vald!", "success");
  };

  const fetchRubrics = async () => {
    setIsLoadingRubrics(true);

    try {
      const rubricsCollection = collection(db, "rubrics");
      // Fetch user-specific rubrics
      const userRubricsQuery = query(
        rubricsCollection,
        and(
          where("formativeComments", "==", true),
          or(
            where("userId", "==", user.uid),
            where("sharedTo", "==", user.uid),
          ),
        ),
      );
      const userRubricsSnapshot = await getDocs(userRubricsQuery);
      const userRubricsList = userRubricsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUserRubrics(userRubricsList);
      // Fetch standard rubrics
      const standardRubricsQuery = query(
        rubricsCollection,
        where("custom", "==", true),
      );
      const standardRubricsSnapshot = await getDocs(standardRubricsQuery);
      const standardRubricsList = standardRubricsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setStandardRubrics(standardRubricsList);
      // // Set first rubric as selected if there are any rubrics
      // if (userRubricsList.length > 0) {
      //   setSelectedRubric(userRubricsList[0].id);
      // } else if (standardRubricsList.length > 0) {
      //   //setSelectedRubric(standardRubricsList[0].id);
      // }
    } catch (error) {
      console.error("Error fetching rubrics:", error);
      showSnackbar(
        "Ett fel uppstod vid hämtning av bedömningsmatriser",
        "error",
      );
    } finally {
      setIsLoadingRubrics(false);
    }
  };

  // Use it in useEffect
  useEffect(() => {
    fetchRubrics();
  }, [user.uid, showSnackbar]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      // Calculate total files after addition
      const totalFileCount = files.length + acceptedFiles.length;

      if (acceptedFiles.length > 8) {
        showSnackbar(
          "Du kan bedöma max 8 texter åt gången. Uppgradera till Premium för fler.",
          "error",
        );
        return;
      }

      // Check if user is attempting to upload more than 20 files total while not having premium
      // if (!user.isPremium && totalFileCount > 20) {
      //   setIsLimitDialogOpen(true);
      //   return;
      // }

      if (!user.isPremium && totalFileCount > 8) {
        showSnackbar(
          "Du kan bedöma max 8 texter åt gången. Uppgradera till Premium för fler.",
          "error",
        );
        return;
      }

      // Filter supported files
      const supportedFiles = acceptedFiles.filter((file) => {
        const isSupported = [".docx", ".doc"].some((ext) =>
          file.name.toLowerCase().endsWith(ext),
        );

        // Check file size (e.g., 5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        const isValidSize = file.size <= maxSize;

        if (!isSupported) {
          showSnackbar(
            `Filformatet för ${file.name} stöds inte. Använd .docx eller .doc`,
            "error",
          );
        }

        if (!isValidSize) {
          showSnackbar(
            `${file.name} är för stor. Maximal storlek är 5MB`,
            "error",
          );
        }

        return isSupported && isValidSize;
      });

      if (supportedFiles.length > 0) {
        // Append new files instead of replacing them
        setFiles((prevFiles) => [...prevFiles, ...supportedFiles]);

        // Initialize progress tracking for each new file
        setFileProgress((prevProgress) => {
          const newProgress = { ...prevProgress };
          supportedFiles.forEach((file) => {
            newProgress[file.name] = 0;
          });
          return newProgress;
        });

        setTotalFiles((prev) => prev + supportedFiles.length);
      }
    },
    [showSnackbar, files.length, user.isPremium],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    multiple: true, // Allow multiple files
  });

  const simulateProgress = (fileName, startProgress, endProgress, duration) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setFileProgress((prev) => ({
          ...prev,
          [fileName]: currentProgress,
        }));

        // Calculate overall progress across all files
        const fileNames = Object.keys(fileProgress);
        let sum = 0;
        fileNames.forEach((name) => {
          sum += fileProgress[name];
        });
        const overall = (sum / (fileNames.length * 100)) * 100;
        setOverallProgress(overall);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const extractTextFromWordDocument = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const result = await mammoth.extractRawText({ arrayBuffer });
          resolve(result.value);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const extractScores = (content) => {
    try {
      const scoresMatch = content.match(/<scores>([\s\S]*?)<\/scores>/);
      if (scoresMatch && scoresMatch[1]) {
        return JSON.parse(scoresMatch[1]);
      }
      return null;
    } catch (error) {
      console.error("Error parsing scores:", error);
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const canGenerate = await checkLessonLimit(user.uid);
    if (!canGenerate) {
      Logger.log("throws error here for user id ", user.uid);
      throw new Error("LESSON_LIMIT_REACHED_IN_CALL_API"); // This is all we need - just return null when limit is hit
    }

    setIsLoading(true);
    setCurrentFileIndex(0);
    setLastProcessedDocRef(null);
    let docId = null;
    // If manual entry is selected, process as before
    // If manual entry is selected, process all manual texts
    if (isManualEntry) {
      if (!user.isPremium && manualTexts.length > 8) {
        showSnackbar(
          "Du kan bedöma max 8 texter åt gången. Uppgradera till Premium för fler.",
          "error",
        );
        setIsLoading(false);
        return;
      }

      // Validate that all texts have content and name
      const invalidTexts = manualTexts.filter(
        (text) => !text.content.trim() || !text.name.trim(),
      );
      if (invalidTexts.length > 0) {
        showSnackbar("Vänligen fyll i alla textfält och namn", "error");
        setIsLoading(false);
        return;
      }

      // If multiple texts, create placeholder documents first (SAME as file logic)
      if (manualTexts.length > 1) {
        try {
          for (let i = 0; i < manualTexts.length; i++) {
            await addDoc(collection(db, "essays"), {
              userId: user.uid,
              fileName: manualTexts[i].name,
              educationLevel: grade,
              language: language,
              rubricId: selectedRubric,
              rubricName: [...userRubrics, ...standardRubrics].find(
                (rubric) => rubric.id === selectedRubric,
              )?.name,
              createdAt: new Date(),
              assessmentStyle: assessmentStyle,
              classId: selectedClass,
              queued: true,
              content: "",
              results: "Bearbetas...",
              isUserRubric: userRubrics.some((r) => r.id === selectedRubric),
            });
          }

          showSnackbar(
            `${manualTexts.length} texter bedöms just nu...`,
            "success",
          );
          navigate(`/app/uppsatser`);
        } catch (error) {
          console.error("Error creating placeholders:", error);
          showSnackbar("Ett fel uppstod vid förberedelse av texter", "error");
          setIsLoading(false);
          return;
        }
      }

      // Process each manual text
      for (let i = 0; i < manualTexts.length; i++) {
        setCurrentFileIndex(i);
        setProgressLabel(
          `Förbereder ${manualTexts[i].name} (${i + 1}/${manualTexts.length})...`,
        );

        try {
          docId = await processText(
            manualTexts[i].content,
            manualTexts[i].name,
          );

          if (i < manualTexts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
        } catch (error) {
          console.error(`Error processing text ${manualTexts[i].name}:`, error);
          showSnackbar(
            `Ett fel uppstod vid bearbetning av ${manualTexts[i].name}`,
            "error",
          );
        }
      }

      setIsLoading(false);

      if (manualTexts.length === 1) {
        if (docId) {
          navigate(`/app/uppsats/${docId}`);
        } else {
          navigate(`/app/uppsatser`);
        }
      }
      return;
    }

    // Verify files are selected
    if (!files.length) {
      showSnackbar("Vänligen ladda upp minst en fil", "error");
      setIsLoading(false);
      return;
    }

    // Initialize all files as queued in the database first
    if (files.length > 1) {
      try {
        // Create placeholder entries for all files
        for (let i = 0; i < files.length; i++) {
          await addDoc(collection(db, "essays"), {
            userId: user.uid,
            fileName: files[i].name,
            educationLevel: grade,
            language: language,
            rubricId: selectedRubric,
            rubricName: [...userRubrics, ...standardRubrics].find(
              (rubric) => rubric.id === selectedRubric,
            )?.name,
            createdAt: new Date(),
            assessmentStyle: assessmentStyle,
            classId: selectedClass,
            queued: true, // Mark as queued
            content: "", // Empty placeholder
            results: "Bearbetas...", // Placeholder text
            isUserRubric: userRubrics.some((r) => r.id === selectedRubric),
          });
        }

        // Redirect to essays list immediately if multiple files
        if (files.length > 1) {
          setProgressLabel("Bedömningarna läggs till i kö...");
          setProgress(74);
          // Wait for 3 seconds before redirecting
          await new Promise((resolve) => setTimeout(resolve, 2000));

          showSnackbar(`${files.length} texter bedöms just nu...`, "success");
          navigate(`/app/uppsatser`);
        }
      } catch (error) {
        console.error("Error creating placeholders:", error);
        showSnackbar("Ett fel uppstod vid förberedelse av texter", "error");
        setIsLoading(false);
        return;
      }
    }

    // Process each file sequentially with delay between API calls
    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      setProgressLabel(
        `Förbereder ${files[i].name} (${i + 1}/${files.length})...`,
      );

      try {
        const fileContent = await extractTextFromWordDocument(files[i]);

        // CRITICAL: Await the full completion of each file before moving on
        docId = await processText(fileContent, files[i].name);

        // Make sure progress is updated
        setFileProgress((prev) => ({
          ...prev,
          [files[i].name]: 100,
        }));

        // Add a longer delay between files to prevent race conditions
        if (i < files.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      } catch (error) {
        console.error(`Error processing file ${files[i].name}:`, error);
        showSnackbar(
          `Ett fel uppstod vid bearbetning av ${files[i].name}`,
          "error",
        );
      }
    }

    // After all files are processed
    setIsLoading(false);
    setOverallProgress(100);

    if (files.length === 1) {
      if (docId) {
        navigate(`/app/uppsats/${docId}`);
      } else {
        navigate(`/app/uppsatser`);
      }
    }
  };

  // 6. Lägg till handler för när ny stil skapas
  // In EssayUpload.js - change handleStyleCreated:
  const handleStyleCreated = (newStyle) => {
    setCustomAssessmentStyles((prev) => [...prev, newStyle]);
    setAssessmentStyle(newStyle.id);
    setIsCreateStyleDialogOpen(false); // Close dialog here instead
  };

  // 7. Lägg till handler för att radera stil
  const handleDeleteStyle = async (styleId, event) => {
    event.stopPropagation();

    try {
      await deleteDoc(doc(db, "assessmentStyles", styleId));
      setCustomAssessmentStyles((prev) =>
        prev.filter((style) => style.id !== styleId),
      );

      // Reset assessment style if the deleted one was selected
      if (assessmentStyle === styleId) {
        setAssessmentStyle("balanced");
      }

      showSnackbar("Bedömningsstil raderad", "success");
    } catch (error) {
      console.error("Error deleting assessment style:", error);
      showSnackbar("Ett fel uppstod vid radering av bedömningsstil", "error");
    }
  };

  // 8. Uppdatera getAssessmentStyleInstruction funktionen
  const getAssessmentStyleInstruction = (style) => {
    // Check if it's a custom style
    const customStyle = customAssessmentStyles.find((s) => s.id === style);
    if (customStyle) {
      return `Bedömningsstil: ${customStyle.description}`;
    }

    // Standard styles
    switch (style) {
      case "encouraging":
        return `Bedömningsstil: Bedöm texten med en uppmuntrande och stödjande ton. Fokusera på textens styrkor och positiva aspekter. Ge konstruktiv feedback som motiverar eleven att utvecklas vidare. Var generös med beröm för goda insatser och formulera förbättringsförslag som uppmuntran snarare än kritik.`;
      case "critical":
        return `Bedömningsstil: Bedöm texten med höga akademiska krav och noggrann granskning. Var tydlig med brister och utvecklingsområden. Fokusera på vad som saknas för att nå högre betygsnivåer. Håll en konstruktiv men kravställande ton som utmanar eleven att prestera bättre.`;
      case "extra_encouraging":
        return `Bedömningsstil: Bedöm texten med en mycket uppmuntrande och motiverande ton. Fokusera starkt på textens styrkor och framsteg. Var extra generös med beröm och positiv feedback. Formulera all kritik som konstruktiva möjligheter och framtida potential. Använd språk som inspirerar och bygger självförtroende. Betona alltid vad eleven gör bra innan du föreslår förbättringar.`;
      case "extra_critical":
        return `Bedömningsstil: Bedöm texten med mycket höga akademiska krav och extra noggrann granskning. Var tydlig och specifik med alla brister och utvecklingsområden. Fokusera på vad som behöver förbättras för att nå excellens. Håll en strikt, akademisk ton som ställer höga krav på precision, argumentation och språklig korrekthet. Acceptera inte halvmesyrer och var tydlig med var standarden inte uppnås.`;
      default: // balanced
        return `Bedömningsstil: Bedöm texten objektivt enligt bedömningsmatrisen. Ge en rättvis analys av både styrkor och utvecklingsområden. Håll en neutral, professionell ton som är vägledande utan att vara överdrivet kritisk eller uppmuntrande.`;
    }
  };

  const processText = async (content, fileName) => {
    // Find the placeholder document if it exists

    await trackLesson(user.uid, "EssayUpload.js", null, null);

    let existingDocRef = null;

    if (files.length > 1 || (isManualEntry && manualTexts.length > 1)) {
      try {
        const essaysRef = collection(db, "essays");
        const q = query(
          essaysRef,
          where("userId", "==", user.uid),
          where("fileName", "==", fileName),
          where("queued", "==", true),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          existingDocRef = querySnapshot.docs[0].ref;
        }
      } catch (error) {
        console.error("Error finding placeholder document:", error);
      }
    }

    const gradeInstructionsText = assessmentInstructions[grade];

    // Create a unique request ID with random string to ensure uniqueness
    const requestId = `essay_${user.uid}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

    return new Promise((resolve, reject) => {
      try {
        const selectedRubricObject = [...userRubrics, ...standardRubrics].find(
          (rubric) => rubric.id === selectedRubric,
        );

        if (!selectedRubricObject) {
          reject(new Error("Vänligen välj en bedömningsmatris"));
          return;
        }

        const prompt = `Bedöm följande text baserat på den angivna bedömningsmatrisen. 
    Skriv bedömningen på ${language}. 
    Texten inom <h2>-taggar och <h3>-taggar nedan ska också vara på ${language}.
    Årskurs: ${gradeInstructionsText}.
    ${getAssessmentStyleInstruction(assessmentStyle)}


    ${
      selectedRubricObject.assignmentName ||
      selectedRubricObject.assignmentDescription
        ? `Uppgiftsinformation:
    ${selectedRubricObject.assignmentName ? `Uppgiftens namn: ${selectedRubricObject.assignmentName}` : ""}
    ${selectedRubricObject.assignmentDescription ? `Uppgiftsbeskrivning: ${selectedRubricObject.assignmentDescription}` : ""}
      `
        : ""
    }
        
    Ge en analys för varje kriterium, förslag på förbättringar och ett betyg. Formatera svaret med följande struktur:

    VIKTIGT: Du MÅSTE inkludera två delar i ditt svar:
    
    1. Först den vanliga bedömningen
    2. Därefter, MÅSTE du inkludera en scores-sektion i slutet, som baseras på bedömningsmatrisens krav för varje betyg, med exakt detta format:
    
    <scores>
    {
      "criteriaScores": {
        "${selectedRubricObject.criteria[0].name}": {
          "score": 4,
          "grade": "A",
          "motivation": "Konkret motivering på exakt 2 meningar"
        }
        // Repetera för varje kriterium
      },
      "overallScore": {
        "score": 4,
        "grade": "A",
        "motivation": "Konkret motivering på exakt 4 meningar"
      }
    }
    </scores>
    
    Notera: scores-sektionen MÅSTE vara med i ditt svar och MÅSTE innehålla ett score mellan 0-5 för varje kriterium samt ett totalbetyg.
    



    
    <h2>Kriterieanalys</h2>
    
    <h3>[Kriteriumets namn]</h3>
    <p><strong>Analys:</strong> [Analys av detta kriterium på 3-4 meningar]</p>
    
    <blockquote><p><strong>Förbättringsförslag:</strong>[Förslag på förbättring på 3-4 meningar]</p></blockquote>
    
    <p><strong>Betyg:</strong> [Betyg för detta kriterium (A-F)]</p>
    [Upprepa för varje kriterium]
    
    <h3>Övergripande betyg</h3>
    <p>[Ange övergripande betyg här]</p>
    
    <hr>
    
    <h2>Bedömningsresultat</h2>
    
    <h3>Övergripande analys</h3>
    <p>[Ange övergripande analys av texten här]</p>
    
    <h3>Positiv feedback</h3>
    <p>[Ange positiv feedback om textens styrkor här]</p>
    
    <h3>Förbättringsplan</h3>
    <ul>
      <li>[Förbättringsåtgärd 1]</li>
      <li>[Förbättringsåtgärd 2]</li>
      <li>[Förbättringsåtgärd 3]</li>
      <li>[Förbättringsåtgärd 4]</li>
      <li>[Förbättringsåtgärd 5]</li>
    </ul>
    
    <hr>
    
    <h2>Felrapport</h2>
    
    <h3>Grammatik</h3>
    <p>[Lista med grammatikfel och förslag på korrigeringar]</p>
    
    <h3>Meningsbyggnad</h3>
    <p>[Beskrivning av problem med meningsbyggnad]</p>
    
    <h3>Sammanhangsproblem</h3>
    <p>[Identifierade problem med textens sammanhang]</p>
    
    <h3>Tydlighetsproblem</h3>
    <p>[Områden där texten kan förbättras för ökad tydlighet]</p>
    
    <h3>Logiska inkonsekvenser</h3>
    <p>[Identifierade logiska problem eller motsägelser i texten]</p>
    
    <h3>Stavfel</h3>
    <p>[Lista med stavfel och förslag på korrigeringar]</p>




    
    Bedömningsmatris:
    ${JSON.stringify(selectedRubricObject)}
    

    ${
      selectedRubricObject.assignmentName ||
      selectedRubricObject.assignmentDescription
        ? `Uppgiftsinformation:
    ${selectedRubricObject.assignmentName ? `Uppgiftens namn: ${selectedRubricObject.assignmentName}` : ""}
    ${selectedRubricObject.assignmentDescription ? `Uppgiftsbeskrivning: ${selectedRubricObject.assignmentDescription}` : ""}
      `
        : ""
    }

    Text att bedöma:
    ${content}`;

        Logger.log("Bedömninisgprompt: ", prompt);

        // Create a document in Firebase to track this request
        const requestRef = doc(db, "apiRequests", requestId);
        setDoc(requestRef, {
          userId: user.uid,
          status: "pending",
          prompt: prompt,
          createdAt: new Date(),
        }).catch((error) => {
          reject(error);
          return;
        });

        // Set up progress labels for this specific file
        const fileNumber = currentFileIndex + 1;
        const totalNumberOfFiles = files.length;
        const filePrefix = `[${fileNumber}/${totalNumberOfFiles}] ${fileName}: `;

        setTimeout(() => setProgressLabel(`Analyserar texten...`), 0);
        setTimeout(
          () => setProgressLabel(`Applicerar bedömningsmatrisen...`),
          3000,
        );
        setTimeout(
          () => setProgressLabel(`Genererar detaljerad analys och feedback...`),
          6000,
        );
        setTimeout(
          () => setProgressLabel(`Genererar ytterligare rapporter...`),
          14000,
        );
        setTimeout(
          () => setProgressLabel(`Bedömer texten baserat på matrisen...`),
          17000,
        );

        const stopSimulation = simulateProgress(fileName, 0, 93, 15000);

        // Set up a listener for the Firebase document
        const unsubscribe = onSnapshot(requestRef, async (snapshot) => {
          if (!snapshot.exists()) return;

          const data = snapshot.data();

          if (data.status === "completed" && data.response) {
            try {
              // Process the results
              stopSimulation();
              setFileProgress((prev) => ({
                ...prev,
                [fileName]: 95,
              }));

              // Process the response data
              const responseData = data.response;
              let messageContent =
                responseData.choices[0].message.content.trim();

              // Extract scores before cleaning up markdown
              const scores = extractScores(messageContent);

              // Remove both scores and studentText sections from displayed content
              messageContent = messageContent.replace(
                /<scores>[\s\S]*?<\/scores>/,
                "",
              );

              if (messageContent.startsWith("```html")) {
                messageContent = messageContent.slice(7, -3).trim();
              } else if (messageContent.startsWith("```")) {
                messageContent = messageContent.slice(3, -3).trim();
              }

              Logger.log(messageContent);

              // If we have an existing placeholder document, update it
              let docRef;
              if (existingDocRef) {
                // Update the existing placeholder document
                await setDoc(
                  existingDocRef,
                  {
                    content: content,
                    results: messageContent,
                    scores: scores,

                    queued: false, // No longer queued
                    inputTokens: responseData.usage.prompt_tokens,
                    completionTokens: responseData.usage.completion_tokens,
                    totalTokens: responseData.usage.total_tokens,
                  },
                  { merge: true },
                );
                docRef = existingDocRef;
              } else {
                // Create a new document if no placeholder exists
                docRef = await addDoc(collection(db, "essays"), {
                  userId: user.uid,
                  content: content,
                  results: messageContent,
                  scores: scores,
                  fileName: fileName,
                  educationLevel: grade,
                  language: language,
                  rubricId: selectedRubric,
                  rubricName: selectedRubricObject.name,
                  assessmentStyle: assessmentStyle,
                  createdAt: new Date(),
                  classId: selectedClass,
                  inputTokens: responseData.usage.prompt_tokens,
                  completionTokens: responseData.usage.completion_tokens,
                  totalTokens: responseData.usage.total_tokens,
                  queued: false, // Not queued
                  isUserRubric: userRubrics.some(
                    (r) => r.id === selectedRubric,
                  ),
                });
              }

              // Store the docRef for the last processed essay
              setLastProcessedDocRef(docRef.id);

              setFileProgress((prev) => ({
                ...prev,
                [fileName]: 100,
              }));

              // Show option to view all essays after first file completes
              if (files.length > 1 && currentFileIndex === 0) {
                setNavigationDialogOpen(true);
              }

              setProgressLabel(`${filePrefix}Klar! Förbereder nästa...`);

              // Clean up
              unsubscribe();

              // RESOLVE THE PROMISE - this is crucial!
              //resolve();
              resolve(docRef.id);
            } catch (error) {
              console.error("Error processing response:", error);
              showSnackbar(
                `Ett fel uppstod vid bearbetning av ${fileName}`,
                "error",
              );
              unsubscribe();

              // Update the placeholder to remove queued status if there was an error
              if (existingDocRef) {
                await setDoc(
                  existingDocRef,
                  {
                    queued: false,
                    error: error.message || "Ett fel uppstod vid bearbetning",
                  },
                  { merge: true },
                );
              }

              // REJECT THE PROMISE on error
              reject(error);
            }
          } else if (data.status === "error") {
            console.error("Error grading essay:", data.error);
            showSnackbar(
              `Fel vid bedömning av ${fileName}: ${data.error || "Ett oväntat fel uppstod."}`,
              "error",
            );
            stopSimulation();
            unsubscribe();

            // Update the placeholder to remove queued status if there was an error
            if (existingDocRef) {
              await setDoc(
                existingDocRef,
                {
                  queued: false,
                  error: data.error || "Ett fel uppstod vid bearbetning",
                },
                { merge: true },
              );
            }

            // REJECT THE PROMISE on error
            reject(new Error(data.error || "Ett fel uppstod vid bedömning"));
          }
        });

        const API_URL =
          process.env.NODE_ENV === "production"
            ? "/api"
            : "http://localhost:3001/api";

        // Make the API call separately using fetch
        fetch(`${API_URL}/openai-essay`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requestId: requestId,
            userId: user.uid,
            prompt: prompt,
            existingDocId: existingDocRef ? existingDocRef.id : null,
          }),
        }).catch((error) => {
          console.error("Error initiating API request:", error);
          reject(error);
        });

        // Add a timeout to prevent hanging forever
        setTimeout(() => {
          unsubscribe();
          reject(new Error("Request timed out after 180 seconds"));
        }, 180000);
      } catch (error) {
        console.error(`Error setting up grading for ${fileName}:`, error);
        showSnackbar(
          `Fel vid inställning av bedömning för ${fileName}: ${error.message || "Ett oväntat fel uppstod."}`,
          "error",
        );

        // Update the placeholder to remove queued status if there was an error
        if (existingDocRef) {
          setDoc(
            existingDocRef,
            {
              queued: false,
              error: error.message || "Ett fel uppstod vid förberedelse",
            },
            { merge: true },
          ).catch((err) => console.error("Error updating doc status:", err));
        }

        reject(error);
      }
    });
  };

  const uploadBox = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    justifyContent: "center",
    minHeight: "300px",
    width: "100%",
    borderRadius: "8px",
    backgroundColor: isDragActive
      ? theme.palette.mode === "dark"
        ? "rgba(107, 33, 168, 0.1)"
        : "rgba(237, 233, 254, 0.5)"
      : theme.palette.mode === "dark"
        ? "rgba(88, 28, 135, 0.08)"
        : "rgba(245, 243, 255, 0.3)",
    border: "2px dashed",
    borderColor: isDragActive
      ? theme.palette.mode === "dark"
        ? "rgba(139, 92, 246, 0.6)"
        : "rgba(109, 40, 217, 0.5)"
      : theme.palette.mode === "dark"
        ? "rgba(139, 92, 246, 0.35)"
        : "rgba(167, 139, 250, 0.4)",
    boxShadow: isDragActive ? "0 2px 6px rgba(139, 92, 246, 0.15)" : "",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(124, 58, 237, 0.6)"
          : "rgba(109, 40, 217, 0.5)",
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(107, 33, 168, 0.1)"
          : "rgba(237, 233, 254, 0.4)",
      boxShadow: "0 3px 8px rgba(139, 92, 246, 0.12)",
    },
  };

  // Add this component for the option dialog
  const renderNavigationOptions = () => (
    <Dialog
      open={isLoading && files.length > 1 && currentFileIndex > 0}
      PaperProps={{
        sx: {
          borderRadius: 4,
          padding: 2,
          maxWidth: "400px",
        },
      }}
    >
      <DialogContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Flera texter bearbetas
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {currentFileIndex} av {files.length} texter har färdigbedömts. Du kan
          antingen:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              // Keep processing in background
              navigate(`/app/uppsatser`);
            }}
            startIcon={<TableChartIcon />}
          >
            Gå till textlistan för att se status
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              // Just close the dialog and continue waiting
              setNavigationDialogOpen(false);
            }}
          >
            Stanna kvar och vänta
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
  // Replace the existing progress bar with this component for multiple files
  const renderProgressDialogContent = () => (
    <Box
      sx={{
        width: "100%",
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e01cd5" />
            <stop offset="100%" stopColor="#1CB5E0" />
          </linearGradient>
        </defs>
      </svg>

      <CircularProgress
        size={58}
        sx={{
          "svg circle": { stroke: "url(#my_gradient)" },
        }}
      />

      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "600",
          fontSize: "1.3rem",
          mb: 2,
        }}
      >
        Bedömer... Snart klart...
      </Typography>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Current file progress */}
        <ProgressBar
          progress={
            isManualEntry
              ? Object.values(fileProgress)[0] || 0 // Use first value for manual entry
              : fileProgress[files[currentFileIndex]?.name] || 0 // Use file name for file uploads
          }
          label={progressLabel}
          height="15px"
          fontSize="0.9rem"
          progressBarWidth="100%"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ ...styles.container }}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            ...styles.newH1,
            width: { xs: "100%", md: "25%" },
            mt: { xs: 0, md: "inherit" },
          }}
        >
          Bedömning
        </Typography>
        {!isMobile && (
          <InfoBox
            title="Textlabbet"
            instructions={[
              "Ange din text manuellt eller ladda upp fil",
              "Välj vad du vill göra med din text genom flera bearbetningsalternativ",
              "Kopiera eller exportera texten som PDF- eller Word-dokument",
            ]}
            theme={theme}
            styles={styles}
            user={user}
            grading={true}
          />
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            component={Link}
            startIcon={<AddIcon />}
            to="/app/matris/ny"
            sx={{ display: "none" }}
          >
            Ny matris
          </Button>

          <Tooltip title="Mina bedömningar">
            <IconButton onClick={() => navigate("/app/uppsatser")}>
              <FormatAlignRightIcon
                sx={{ color: "text.secondary", fontSize: "1.4rem" }}
              />
            </IconButton>
          </Tooltip>

          <Button
            variant="print"
            sx={{ ...styles.tightButton, display: "none" }}
            component={Link}
            startIcon={<SegmentIcon />}
            to="/app/uppsatser"
          >
            Dina bedömningar
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <>
              <AnimatedStep isRevealed={true}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 0,
                    width: { xs: "100%", sm: "100%", md: "75%" },
                    margin: "0 auto",

                    mb: 7,
                    mt: { xs: 2, md: 8 },
                    borderRadius: 3,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                    backgroundImage: "none",
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(to right, #8B5CF6, #6D28D9)",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  >
                    <Box
                      sx={{
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(8px)",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 24,
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        1
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "white",
                      }}
                    >
                      Grundinformation
                    </Typography>
                  </Box>
                  <Grid container spacing={3} sx={{ p: 5 }}>
                    {/* Class Selection */}
                    <Grid
                      item
                      xs={12}
                      md={user.accountType === "teacher" ? 6 : 12}
                    >
                      <AssessmentStyleSelector
                        assessmentStyle={assessmentStyle}
                        setAssessmentStyle={setAssessmentStyle}
                        customAssessmentStyles={customAssessmentStyles}
                        setIsCreateStyleDialogOpen={setIsCreateStyleDialogOpen}
                        handleDeleteStyle={handleDeleteStyle}
                      />
                    </Grid>
                    {user.accountType === "teacher" && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500, mb: isMobile ? 1 : 1 }}
                          >
                            Klass:
                          </Typography>
                          {!isMobile && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1, display: "none" }}
                            >
                              Används för att organisera bedömningar i din{" "}
                              <Box
                                component="span"
                                sx={{
                                  color: "primary.main",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                  textDecoration: "none",
                                  display: "inline",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                                onClick={() => setVideoDialogOpen(true)}
                              >
                                resultattavla
                              </Box>{" "}
                            </Typography>
                          )}
                          <FormControl fullWidth>
                            <MUISelect
                              value={selectedClass}
                              onChange={(e) => {
                                if (e.target.value !== "create") {
                                  setSelectedClass(e.target.value);
                                }
                              }}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected) return "Välj klass (valfritt)";
                                const selectedClassObj = classes.find(
                                  (cls) => cls.id === selected,
                                );
                                return selectedClassObj
                                  ? selectedClassObj.name
                                  : "Välj klass (valfritt)";
                              }}
                            >
                              <MenuItem value="" disabled>
                                Välj klass (valfritt)
                              </MenuItem>
                              {classes.map((cls) => (
                                <MenuItem key={cls.id} value={cls.id}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flex: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {cls.name}
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleDeleteClass(cls.id, e)
                                      }
                                      sx={{
                                        color: "text.secondary",
                                        "&:hover": {
                                          color: "#EF4444",
                                          backgroundColor:
                                            "rgba(239, 68, 68, 0.1)",
                                        },
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>
                                </MenuItem>
                              ))}
                              <MenuItem
                                value="create"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsCreateClassDialogOpen(true);
                                }}
                                sx={{
                                  pt: 2,
                                  pb: 1,
                                  borderTop: "1px solid",
                                  borderColor: "divider",
                                  color: "primary.main",
                                  fontWeight: "medium",
                                }}
                              >
                                <AddIcon sx={{ fontSize: "1.2rem", mr: 1 }} />{" "}
                                Skapa ny klass
                              </MenuItem>
                            </MUISelect>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Language Selection */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, mb: 1, mt: { xs: 0, md: 1 } }}
                      >
                        Språk:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, display: "none" }}
                      >
                        Språket jag vill ha bedömningen på:
                      </Typography>
                      <LanguageSelector
                        language={language}
                        setLanguage={setLanguage}
                      />
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ mt: { xs: 0.5, md: 1.4 } }}>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500" }}
                      >
                        AI-modell:
                      </Typography>
                      <AIModelInfoTooltip>
                        <span style={{ cursor: "help" }}>
                          <QuestionMarkIcon
                            sx={{
                              fontSize: 16,
                              ml: 1,
                              verticalAlign: "middle",
                            }}
                          />
                        </span>
                      </AIModelInfoTooltip>

                      <AIModelSelect
                        selectedModel={selectedModel}
                        setSelectedModel={setSelectedModel}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="purp"
                      sx={{ mt: 2, mb: 4, width: "150px" }}
                      onClick={() => {
                        setCurrentStep(2);
                        setIsRubricsDrawerOpen(true);
                      }}
                      endIcon={<KeyboardArrowRightIcon />}
                    >
                      Nästa
                    </Button>
                  </Box>
                </Paper>
              </AnimatedStep>
            </>
          )}

          {currentStep === 2 && (
            <>
              <BackButton
                currentStep={currentStep}
                onClick={() => {
                  setCurrentStep(currentStep - 1);
                }}
                show={currentStep !== 1}
              />
              <AnimatedStep isRevealed={true}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 0,
                    width: { xs: "100%", sm: "100%", md: "75%" },
                    margin: "0 auto",
                    mb: 7,
                    mt: { xs: 2, md: 6 },
                    borderRadius: 3,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                    backgroundImage: "none",
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(to right, #8B5CF6, #6D28D9)",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  >
                    <Box
                      sx={{
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(8px)",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 24,
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        2
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "white",
                      }}
                    >
                      Välj bedömningsmatris
                    </Typography>
                  </Box>
                  <Box sx={{ p: 10, textAlign: "center" }}>
                    <Button
                      variant="purp"
                      onClick={() => setIsRubricsDrawerOpen(true)}
                      endIcon={<KeyboardArrowRightIcon />}
                    >
                      {selectedRubric
                        ? "Byt bedömningsmatris"
                        : "Välj bedömningsmatris"}
                    </Button>

                    {selectedRubric && (
                      <Box sx={{ mt: 4 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: "#e2f0de",
                            p: 2,

                            borderRadius: 3,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ color: "#10b981", mr: 1, fontSize: 30 }}
                          />
                          <Typography sx={{ fontWeight: "600" }}>
                            {[...userRubrics, ...standardRubrics].find(
                              (r) => r.id === selectedRubric,
                            )?.name || ""}{" "}
                            (
                            {beautifyGradeText(
                              [...userRubrics, ...standardRubrics].find(
                                (r) => r.id === selectedRubric,
                              )?.grade || "",
                            )}
                            )({beautifyGradeText(grade)})
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </AnimatedStep>
            </>
          )}

          {currentStep === 3 && (
            <>
              <BackButton
                currentStep={currentStep}
                onClick={() => {
                  setCurrentStep(currentStep - 1);
                  setSelectedRubric("");
                }}
                show={currentStep !== 1}
              />
              <AnimatedStep isRevealed={true}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 0,
                    width: { xs: "100%", sm: "100%", md: "75%" },
                    margin: "0 auto",
                    mt: { xs: 2, md: 4 },
                    mb: 5,

                    borderRadius: 3,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                    backgroundImage: "none",
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(to right, #8B5CF6, #6D28D9)",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  >
                    <Box
                      sx={{
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(8px)",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 24,
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        3
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "white",
                      }}
                    >
                      Text för bedömning
                    </Typography>

                    {selectedRubric && (
                      <>
                        {!isMobile && (
                          <Box sx={{ marginLeft: "auto" }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                background: "rgba(255, 255, 255, 0.1)",
                                backdropFilter: "blur(8px)",
                                p: 1,
                                borderRadius: 3,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ color: "white", mr: 1, fontSize: 15 }}
                              />

                              <Typography
                                sx={{
                                  fontWeight: "500",
                                  fontSize: "0.8rem",
                                  color: "white",
                                }}
                              >
                                {[...userRubrics, ...standardRubrics].find(
                                  (r) => r.id === selectedRubric,
                                )?.name || ""}{" "}
                                ({beautifyGradeText(grade)})
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>

                  <Box
                    sx={{ p: { xs: 2, md: 5 }, pb: { xs: 5 }, pt: { xs: 4 } }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mb: 4 }}
                    >
                      <CustomInputSwitch
                        optionIcons={[<CloudUploadIcon />, <NotesIcon />]}
                        selectedIndex={isManualEntry ? 1 : 0}
                        onIndexChange={(index) => setIsManualEntry(index === 1)}
                        options={[
                          isMobile ? "Ladda upp fil" : "Ladda upp filer",
                          "Skriv in text",
                        ]}
                        optionSubtitles={["Välj valfri fil", "Klistra in text"]}
                        theme={theme}
                        width="420px"
                        mobileWidth="400px"
                        height="52px"
                        iconSpacing={isMobile ? "10px" : "15px"}
                        iconSize="1.6rem"
                        activeColor="#8b5cf6"
                        fontSize={isMobile ? "0.9rem" : "1rem"}
                        fontWeight="600"
                        borderRadius="100px"
                      />
                    </Box>

                    {isManualEntry ? (
                      renderManualTextSection()
                    ) : (
                      <Box {...getRootProps()}>
                        <input {...getInputProps()} />
                        {files.length > 0 ? (
                          <>
                            <Box
                              sx={{
                                ...styles.freshBox,
                                p: 0,
                                width: "100%",
                                maxWidth: "100%",
                                marginBottom: "50px",
                                marginTop: "50px",
                                borderRadius: 2,
                              }}
                            >
                              {/* Header */}
                              <Box
                                sx={{
                                  background:
                                    theme.palette.mode === "dark"
                                      ? "#2d4256"
                                      : "#ededed",

                                  color:
                                    theme.palette.mode === "dark"
                                      ? "white"
                                      : "black",
                                  padding: "12px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography fontWeight={600}>
                                  {files.length}{" "}
                                  {files.length === 1 ? "fil" : "filer"} valda
                                </Typography>
                                <Button
                                  variant="print"
                                  startIcon={<AddIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fileInput =
                                      document.querySelector(
                                        'input[type="file"]',
                                      );
                                    if (fileInput) fileInput.click();
                                  }}
                                  sx={{
                                    ...styles.tightButton,
                                    border: "1px dashed",
                                  }}
                                >
                                  Lägg till fil
                                </Button>
                              </Box>

                              {/* File list */}
                              <Box
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#2d4256"
                                      : "white",
                                  padding: 1.5,
                                }}
                              >
                                <Grid container spacing={2}>
                                  {files.map((file, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={6}
                                      key={file.name}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          padding: 1.5,
                                          borderRadius: 1.5,
                                          border: "1px solid",
                                          borderColor:
                                            theme.palette.mode === "dark"
                                              ? "rgba(255,255,255,0.1)"
                                              : "rgba(0,0,0,0.1)",
                                          backgroundColor:
                                            theme.palette.mode === "dark"
                                              ? "rgba(255,255,255,0.03)"
                                              : "rgba(0,0,0,0.01)",
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            borderColor: "#6366F1",
                                            backgroundColor:
                                              theme.palette.mode === "dark"
                                                ? "rgba(99, 102, 241, 0.08)"
                                                : "rgba(99, 102, 241, 0.04)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor:
                                              "rgba(99, 102, 241, 0.1)",
                                            marginRight: 1.5,
                                          }}
                                        >
                                          <WordIcon
                                            sx={{
                                              color: "#6366F1",
                                              fontSize: 24,
                                            }}
                                          />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography
                                            sx={{
                                              fontWeight: 500,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {file.name}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {(file.size / 1024).toFixed(1)} KB
                                          </Typography>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newFiles = [...files];
                                            newFiles.splice(index, 1);
                                            setFiles(newFiles);
                                          }}
                                          sx={{
                                            color:
                                              theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.5)"
                                                : "rgba(0,0,0,0.5)",
                                            "&:hover": {
                                              color: "#EF4444",
                                              backgroundColor:
                                                theme.palette.mode === "dark"
                                                  ? "rgba(239, 68, 68, 0.1)"
                                                  : "rgba(239, 68, 68, 0.05)",
                                            },
                                          }}
                                        >
                                          <CloseIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box sx={uploadBox}>
                              <CloudUploadIcon
                                sx={{
                                  fontSize: 50,
                                  color: "#8b5cf6",
                                  mb: 3,
                                  transform: isDragActive
                                    ? "scale(1.1)"
                                    : "scale(1)",
                                  transition: "transform 0.2s ease-in-out",
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                {isMobile
                                  ? "Klicka här för att ladda upp fil"
                                  : isDragActive
                                    ? "Släpp filerna här"
                                    : "Dra och släpp filer här"}
                              </Typography>
                              {!isMobile && (
                                <>
                                  <Typography
                                    color="text.secondary"
                                    sx={{ mb: 3 }}
                                  >
                                    eller
                                  </Typography>
                                  <Button
                                    variant="print"
                                    sx={{ ...styles.tightButton }}
                                    startIcon={<FileUpload />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const fileInput =
                                        document.querySelector(
                                          'input[type="file"]',
                                        );
                                      if (fileInput) fileInput.click();
                                    }}
                                  >
                                    Ladda upp filer
                                  </Button>
                                </>
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}

                    {renderNavigationOptions()}
                    {isLoading && (
                      <Dialog
                        open={isLoading}
                        disableEscapeKeyDown
                        onClick={handleBackdropClick}
                        PaperProps={{
                          elevation: 0,
                          sx: {
                            minWidth: { xs: "95%", md: "600px" },
                            minHeight: { xs: "30%", md: "350px" }, // Made taller for multiple files
                            backgroundColor: "background.paper",
                            boxShadow: 24,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          },
                        }}
                      >
                        <DialogContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            width: "100%",
                          }}
                        >
                          {renderProgressDialogContent()}

                          {/* Display a helpful message for multiple files after first file is done */}
                          {files.length > 1 && currentFileIndex > 0 && (
                            <Box sx={{ mt: 2, textAlign: "center" }}>
                              <Button
                                onClick={() => {
                                  // Keep processing in background
                                  navigate(`/app/uppsatser`);
                                }}
                                variant="text"
                                color="primary"
                                sx={{ textDecoration: "underline" }}
                              >
                                Gå till textlistan för att se status →
                              </Button>
                            </Box>
                          )}
                        </DialogContent>
                      </Dialog>
                    )}

                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                    >
                      <Button
                        type="submit"
                        variant={
                          theme.palette.mode === "light" ? "purp" : "contained"
                        }
                        color="primary"
                        sx={{ width: isLoading ? "auto" : "250px" }}
                        disabled={isLoading}
                        startIcon={
                          <AutoFixHighIcon style={{ color: "#fff" }} />
                        }
                      >
                        {isManualEntry
                          ? manualTexts.length === 1
                            ? "Bedöm texten"
                            : `Bedöm ${manualTexts.length} texter`
                          : files.length === 0
                            ? "Bedöm texter"
                            : files.length === 1
                              ? "Bedöm texten"
                              : `Bedöm ${files.length} texter`}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </AnimatedStep>
            </>
          )}
        </form>
      </Box>
      <RubricsDrawer
        open={isRubricsDrawerOpen}
        onClose={() => setIsRubricsDrawerOpen(false)}
        user={user}
        selectedRubric={selectedRubric}
        onRubricSelect={setSelectedRubric}
        grade={grade}
        setGrade={setGrade}
      />
      <CreateClassDialog
        open={isCreateClassDialogOpen}
        onClose={() => {
          setIsCreateClassDialogOpen(false);
        }}
        onClassCreated={handleClassCreated}
        userId={user.uid}
      />
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableScrollLock
        disableRestoreFocus
        onClick={(e) => {
          e.stopPropagation();
          setVideoDialogOpen(false);
        }}
        PaperProps={{
          sx: { backgroundColor: "transparent", maxWidth: "700px" },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            aria-label="close"
            onClick={() => setVideoDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <VideoPlayer
            videoUrl={videoUrl}
            poster={posterUrl}
            autoPlay
            controls
          />
        </Box>
      </Dialog>

      <CreateAssessmentStyleDialog
        open={isCreateStyleDialogOpen}
        onClose={() => setIsCreateStyleDialogOpen(false)}
        onStyleCreated={handleStyleCreated}
        userId={user.uid}
      />
      <LimitDialog
        open={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        userId={user?.uid}
        user={user}
        styles={styles}
        titleText="Uppgradera för att välja fler filer"
      />
    </Box>
  );
};

export default EssayUpload;
